import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { Copy, Pencil, Share2 } from "lucide-react";
import { Button, ConfirmDialog, useToast } from "@chotca/ui";
import { useClosePosting } from "@chotca/api-client";
import { WEB_APEX_URL } from "@/env";

export function PostingActions({
  postingId,
  state,
}: {
  postingId: string;
  state: string;
}) {
  const t = useTranslations("postings");
  const nav = useNavigate();
  const close = useClosePosting(postingId);
  const { toast } = useToast();
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const isOpen = state === "OPEN";
  const shareUrl = `${WEB_APEX_URL}/postings/${postingId}`;

  async function onShare() {
    // Trên mobile web dùng share sheet của hệ điều hành; desktop thì sao chép link.
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: "Tin tuyển dụng Chốt Ca",
          url: shareUrl,
        });
        return;
      } catch {
        // user huỷ share sheet — không làm gì.
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ tone: "success", title: "Đã sao chép link" });
    } catch {
      window.prompt("Sao chép link:", shareUrl);
    }
  }

  async function onConfirm() {
    try {
      await close.mutateAsync();
      toast({ tone: "success", title: t("cancelSuccess") });
      setConfirmOpen(false);
    } catch {
      toast({ tone: "error", title: t("cancelError") });
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {/* "Đăng lại" is available for any state — an expired/closed tin is the
            main reason to copy (tuyển lại). Prefills the create form. */}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => nav(`/postings/new?duplicate=${postingId}`)}
        >
          <Copy size={14} strokeWidth={2.25} className="mr-1.5" />
          Đăng lại
        </Button>
        {isOpen ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => nav(`/postings/${postingId}/edit`)}
            >
              <Pencil size={14} strokeWidth={2.25} className="mr-1.5" />
              Sửa tin
            </Button>
            <Button variant="secondary" size="sm" onClick={onShare}>
              <Share2 size={14} strokeWidth={2.25} className="mr-1.5" />
              Chia sẻ
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfirmOpen(true)}
              loading={close.isPending}
            >
              {t("cancelPosting")}
            </Button>
          </>
        ) : null}
      </div>
      {isOpen ? (
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={onConfirm}
          title={t("cancelTitle")}
          description={t("cancelDesc")}
          confirmLabel={t("cancelPosting")}
          destructive
          loading={close.isPending}
        />
      ) : null}
    </>
  );
}
